import { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: {
      bridge: "Bridge", about: "About", services: "Services", education: "Education",
      industries: "Industries", insights: "Insights", store: "Store", contact: "Contact",
      cta: "Book a Consultation",
    },
    hero: {
      overline: "JAPAN ⇄ CANADA · BUSINESS BRIDGE",
      lines: ["Cross between Japan", "and Canada and", "arrive succeeding."],
      sub: "Connecting Japan and Canada through business, education, culture, and opportunity. We are not a translation agency and not a consultancy. We are a trusted bridge — present in the room until the outcome is real.",
      scroll: "Scroll",
    },
    about: {
      overline: "ABOUT KAKEHASHI",
      title: "A bridge is only useful if someone stands on both ends.",
      intro: "Ruten Borealis was founded on a simple observation: deals between Japan and Canada rarely fail on numbers. They fail on meaning — a pause misread, a hierarchy ignored, a commitment implied but never voiced.",
      ch1t: "A bridge, not a middleman",
      ch1: "We do not pass messages between parties. We stand in the room with you — interpreting not only the language but the intent, the protocol, and the silence. A middleman wants the transaction closed. A bridge wants the relationship to last.",
      ch2t: "Two homes",
      ch2: "With offices in Tokyo and Toronto, we belong to both business cultures rather than visiting either. Our people have led negotiations, plant openings, and relocations in both directions — and carry the scars and friendships to prove it.",
      ch3t: "Modesty is a method",
      ch3: "In Japanese business culture, the loudest voice rarely wins the longest trust. We practice restrained, exact, reliable work — present until the outcome is real, invisible when the credit is handed out.",
      offices: "Our offices",
      values: "What we believe",
    },
    services: {
      overline: "SERVICES",
      title: "Two disciplines. One promise: nothing gets lost between the languages.",
      interpretation: "Interpretation Services",
      interpretationSub: "Professional human interpreters for moments where a wrong word costs more than a right one earns.",
      consulting: "Cultural Consulting",
      consultingSub: "Strategy and advisory for organizations building something lasting across the Pacific.",
      items: {
        simultaneous: ["Simultaneous Interpretation", "Real-time interpretation for conferences, summits, and live proceedings — faithful at the speed of speech."],
        consecutive: ["Consecutive Interpretation", "Measured, precise interpretation for negotiations, site visits, technical reviews, and audits."],
        onsite: ["On-Site Meeting & Negotiation Support", "A bilingual professional at your side in the room — reading context, posture, and pause, not just words."],
        executive: ["Executive & Board-Level Interpretation", "Discreet senior interpreters for governance, M&A, and investor relations — where confidentiality is absolute."],
        strategy: ["Cross-Cultural Strategy & Advisory", "Decisions informed by how business actually gets done on both sides of the Pacific — not how the handbook says it should."],
        market: ["Market Entry Strategy", "Structure, partners, sequencing, and the first ninety days — for entering Japan or Canada with confidence."],
        expat: ["Expatriate Integration", "Practical, human support for executives and families relocating between the two countries."],
        org: ["Organizational Culture Alignment", "Workshops and advisory that align merged or partner organizations around shared norms and working rhythm."],
      },
    },
    story: {
      tagline: "People Plans Possibilities",
      corridor: "Japan ↔ Canada",
      scrollCue: "Scroll",
      tokyo: "Tokyo",
      toronto: "Toronto",
      ch2Label: "Chapter 02",
      ch2Title: "What brings you here?",
      ch2Lede: "Choose the path closest to yours. Each one leads to the service built for it.",
      paths: [
        { title: "Working holiday", body: "A year to live and work abroad, planned properly from the start.", cta: "See the service" },
        { title: "Study & language", body: "Schools and programs chosen with you, and the daily life around them.", cta: "See the service" },
        { title: "Relocation & settling in", body: "Housing, paperwork, the first months, and everything after.", cta: "See the service" },
        { title: "Business", body: "Interpretation, cultural consulting, and business and manufacturing liaison.", cta: "See the service" },
      ],
      ch3Label: "Chapter 03",
      ch3Title: "How the crossing becomes real",
      ch3Lede: "Four stages. We go through them with you, one at a time.",
      routeCaption: "The whole route",
      stages: [
        { name: "Discover", body: "We clarify your situation, and the questions underneath it that shape everything else." },
        { name: "Plan", body: "Priorities, responsibilities and next steps, set down so everyone knows who does what, and when." },
        { name: "Cross", body: "The work itself: relocation, coordination, interpretation, and support on the ground." },
        { name: "Stay", body: "We remain available until the outcome holds, not just until the paperwork clears." },
      ],
      ch4Label: "Chapter 04",
      ch4Title: "Two sides of the crossing",
      ch4Lede: "Two co-founders, one practice. Each brings the work they know best.",
      coFounder: "Co-founder",
      kazuki: "Kazuki",
      kaho: "Kaho",
      portraitKazuki: "Portrait · Kazuki",
      portraitKaho: "Portrait · Kaho",
      kazukiTags: ["Interpretation", "Cultural consulting", "Business & manufacturing liaison"],
      kahoTags: ["Relocation", "Settling in"],
      kazukiBody: "Kazuki works where language and business meet: interpreting in meeting rooms and on factory floors, and helping each party understand what the other means, not only what they say.",
      kahoBody: "Kaho made the move to Canada herself. She guides relocation and settlement as someone who has been through each step, from the first paperwork to a life that feels like home.",
      bridgeWord: "Kakehashi",
      ch5Label: "Chapter 05 · Start here",
      ch5Title: "Begin with a conversation, not a commitment.",
      ch5Body: "A discovery call is where we listen: where you are, where you're headed, and what stands in the way. You leave with a clearer picture, whether or not we work together.",
      ch5Button: "Book a discovery call →",
      ch5Note: "In Japanese or English",
      floatingCta: "Book a discovery call",
      top: "Top",
    },
    footer: {
      tagline: "A trusted bridge between Japan and Canada — for business, education, culture, and opportunity.",
      navigate: "Navigate",
      offices: "Offices",
      contact: "Contact",
      rights: "All rights reserved.",
    },
    common: {
      forBusiness: "FOR BUSINESS",
      forIndividuals: "FOR INDIVIDUALS",
      learnMore: "Learn more",
      bookCta: "Book a Consultation",
      seminarsCta: "View Upcoming Seminars",
      ctaTitle: "Your trusted partner for Japan-Canada business success — and the personal journeys behind it",
    },
  },
  jp: {
    nav: {
      bridge: "架け橋", about: "私たちについて", services: "サービス", education: "教育",
      industries: "業界", insights: "インサイト", store: "ストア", contact: "お問い合わせ",
      cta: "相談を予約する",
    },
    hero: {
      overline: "日本 ⇄ カナダ · ビジネスブリッジ",
      lines: ["日本とカナダを渡り、", "成功へ", "たどり着く。"],
      sub: "ビジネス、教育、文化、そして機会を通じて、日本とカナダを結びます。私たちは翻訳会社でも、コンサルティング会社でもありません。成果が現実になるまで、その場に寄り添い続ける、信頼の架け橋です。",
      scroll: "スクロール",
    },
    about: {
      overline: "架け橋について",
      title: "橋は、両岸に人が立ってこそ意味を持つ。",
      intro: "ルーテンボレアリスは、ひとつの気づきから生まれました。日本とカナダの取引が破綻するのは、数字の問題ではほとんどありません。読み違えられた沈黙、軽んじられた序列、言葉にならなかった約束——意味の問題なのです。",
      ch1t: "仲介者ではなく、架け橋",
      ch1: "私たちは両者の間で言葉を受け渡すだけの存在ではありません。言語だけでなく、意図、礼法、そして沈黙さえも読み取り、あなたと同じ場に立ちます。仲介者は取引の成立を望みます。架け橋は、関係が続くことを望みます。",
      ch2t: "ふたつの故郷",
      ch2: "東京とトロントに拠点を持つ私たちは、どちらの文化にも「訪問者」ではありません。双方向の交渉、工場開設、駐在を率いてきた経験と、その証としての傷と友情を持っています。",
      ch3t: "謙虚さは、方法である",
      ch3: "日本のビジネス文化において、最も声の大きな者が最も長い信頼を得ることは稀です。私たちは控えめで、正確で、確実な仕事を実践します——成果が現実になるまで寄り添い、称賛の場では静かに退きます。",
      offices: "オフィス",
      values: "私たちの信念",
    },
    services: {
      overline: "サービス",
      title: "ふたつの専門性。ひとつの約束：言葉の間で、何も失わせない。",
      interpretation: "通訳サービス",
      interpretationSub: "一言の間違いが何よりも高くつく場面のために、プロフェッショナルの人間による通訳を。",
      consulting: "文化コンサルティング",
      consultingSub: "太平洋を越えて永続するものを築く組織のための、戦略とアドバイザリー。",
      items: {
        simultaneous: ["同時通訳", "会議、サミット、ライブ進行のためのリアルタイム通訳——発話の速度に忠実に。"],
        consecutive: ["逐次通訳", "交渉、視察、技術レビュー、監査のための、正確で落ち着いた通訳。"],
        onsite: ["会議・交渉への同席サポート", "言葉だけでなく、文脈、姿勢、間を読み取るバイリンガルプロフェッショナルが同席します。"],
        executive: ["役員・取締役会レベル通訳", "ガバナンス、M&A、IRのための、機密保持を徹底したシニア通訳。"],
        strategy: ["異文化戦略・アドバイザリー", "ハンドブックの理想ではなく、太平洋の両岸で実際に機能しているやり方に基づく意思決定を。"],
        market: ["市場参入戦略", "日本・カナダへの参入のための、体制、パートナー、順序、そして最初の90日間。"],
        expat: ["駐在員インテグレーション", "両国間で異動するエグゼクティブとご家族への、実践的で人間的なサポート。"],
        org: ["組織文化アライメント", "合併・提携組織が共通の規範と働くリズムを持てるよう導く、ワークショップとアドバイザリー。"],
      },
    },
    story: {
      scrollCue: "スクロール",
      tokyo: "東京",
      toronto: "トロント",
      ch2Label: "第二章",
      ch2Title: "あなたの目的から、はじめましょう。",
      paths: [
        { title: "ワーキングホリデー" },
        { title: "留学・語学" },
        { title: "移住・定住サポート" },
        { title: "ビジネス・製造業支援" },
      ],
      ch3Label: "第三章",
      ch3Title: "渡る道のりが、かたちになるまで。",
      routeCaption: "全体の流れ",
      stages: [
        { name: "見極める", body: "状況を整理し、その奥にある本当の問いを明らかにします。" },
        { name: "計画する", body: "優先順位、役割分担、次の一手を明確にします。" },
        { name: "渡る", body: "移住、調整、通訳、そして現地でのサポート。" },
        { name: "見届ける", body: "結果が根づくまで、そばにいます。" },
      ],
      ch4Label: "第四章",
      ch4Title: "橋の、両端に。",
      kazuki: "カズキ",
      kaho: "カホ",
      kazukiTags: ["通訳", "文化コンサルティング", "ビジネス・製造業リエゾン"],
      kahoTags: ["移住サポート", "定住サポート"],
      kazukiBody: "会議室から製造の現場まで。言葉だけでなく、その奥にある意図を橋渡しします。",
      kahoBody: "自らカナダへの移住を経験。手続きから暮らしの立ち上げまで、一歩ずつ寄り添います。",
      bridgeWord: "架け橋",
      ch5Label: "はじめに",
      ch5Title: "まずは会話から。約束は、その先で。",
      ch5Button: "初回相談を予約する",
      ch5Note: "日本語・英語で対応",
      floatingCta: "初回相談",
    },
    footer: {
      tagline: "ビジネス、教育、文化、機会のために——日本とカナダを結ぶ、信頼の架け橋。",
      navigate: "ナビゲーション",
      offices: "オフィス",
      contact: "連絡先",
      rights: "全著作権所有。",
    },
    common: {
      forBusiness: "法人向け",
      forIndividuals: "個人向け",
      learnMore: "詳しく見る",
      bookCta: "相談を予約する",
      seminarsCta: "セミナー情報を見る",
      ctaTitle: "日本・カナダビジネスの成功へ、信頼できるパートナーを",
    },
  },
};

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("en");

  useEffect(() => {
    document.documentElement.lang = lang === "jp" ? "ja" : "en";
  }, [lang]);
  const t = (path) => {
    const parts = path.split(".");
    let node = dict[lang];
    for (const p of parts) node = node?.[p];
    if (node === undefined) {
      node = dict.en;
      for (const p of parts) node = node?.[p];
    }
    return node ?? path;
  };
  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
